import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
	Skeleton, Stack, Switch,
	Tooltip, Typography
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import apiRoutes from "../../../../constants/api-routes";
import messageLevels from "../../../../constants/message-levels";
import messages from "../../../../constants/messages";
import navigationItemReferences from "../../../../constants/navigation-item-references";
import pages from "../../../../constants/pages";
import useAuthHeader from "../../../../hooks/useAuthHeader";
import useCoAppNavigation from "../../../../hooks/useCoAppNavigation";
import useDynamicContainerHeight from "../../../../hooks/useDynamicContainerHeight";
import useToastAlert from "../../../../hooks/useToastAlert";
import { selectPermissions, selectUserId, setPermissions } from "../../../../redux/userSlice";
import CoAppDatagridHeader from "../../../global/components/datagrid/CoAppDatagridHeader";
import CoAppDestructiveConfirmationModal from "../../../global/components/modals/CoAppDestructiveConfirmationModal";
import CoAppEditNameModal from "../../../global/components/modals/CoAppEditNameModal";
import { PermissionsContainer } from "./styled/role.styled";

const fakePermissions = Array.from({ length: 5 }, () => ({ id: Math.random(), category: "" }));

// QA fix: the API returns many one-permission categories (General, Extension
// User, Plugin Management, Workstation Management, …). We consolidate them into
// a few clear, function-named sections. Keys are normalized (lowercased/trimmed)
// so they match regardless of the backend's exact casing; any category we don't
// recognize falls back to its own (raw) title so nothing is ever hidden.
const SECTION_FOR_CATEGORY: Record<string, string> = {
	"general": "Access",
	"extension user": "Access",
	"manager requests": "Requests & Approvals",
	"user & group management": "User Administration",
	"user and group management": "User Administration",
	"plugin management": "Plugins & Workstations",
	"workstation management": "Plugins & Workstations",
	"rule builder": "Rules",
	"organization": "Organization & Security",
	"confidential configuration": "Organization & Security",
};

// Display order for the consolidated sections. Unmapped categories are appended
// after these in the order the API returned them.
const SECTION_ORDER: string[] = [
	"Access",
	"User Administration",
	"Rules",
	"Plugins & Workstations",
	"Requests & Approvals",
	"Organization & Security",
];

const sectionForCategory = (category: string): string =>
	SECTION_FOR_CATEGORY[(category ?? "").trim().toLowerCase()] ?? category ?? "Other";

/** Passed from `RoleWithErrorBoundary` when the route was opened without `navigate(..., { state })` (e.g. sidebar). */
interface RoleNavState {
	name: string;
	isSuperAdmin?: boolean;
}

interface RolePermissionsProps {
	state?: RoleNavState | null;
}

interface Permission {
	id: string | number;
	category: string;
	name?: string;
	active?: boolean;
	friendlyName?: string;
	description?: string;
	[key: string]: unknown;
}

interface RoleDetails {
	permissions?: Array<{ name: string; [key: string]: unknown }>;
	users?: Array<{ id: string | number; [key: string]: unknown }>;
	[key: string]: unknown;
}

export default function RolePermissions({ state: stateFromBoundary }: RolePermissionsProps = {}) {
	const authHeader = useAuthHeader();
	const { handleToastAlert } = useToastAlert();
	const { updateCoAppNavigation } = useCoAppNavigation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { roleId: roleIdParam } = useParams<{ roleId: string }>();
	const roleId = roleIdParam ?? "";
	const { containerRef, containerHeight } = useDynamicContainerHeight();
	const { pathname, state: stateFromLocation } = useLocation() as {
		pathname: string;
		state: RoleNavState | undefined;
	};
	/* Router `location.state` only exists when navigation passes `{ state }`. Otherwise the parent boundary injects `state` after fetching the role. */
	const state: RoleNavState = stateFromBoundary ?? stateFromLocation ?? { name: "" };
	const currentUserPermissions = useSelector(selectPermissions);
	const currentUserId = useSelector(selectUserId);
	const [role, setRole] = useState<RoleDetails>({});
	const [rolePermissions, setRolePermissions] = useState<Permission[]>([]);
	const [permissionSeed, setPermissionSeed] = useState(0);
	const [deleteRoleDialogOpen, setDeleteRoleDialogOpen] = useState(false);
	const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false);
	const [roleNameInput, setRoleNameInput] = useState("");
	const [roleNameError, setRoleNameError] = useState("");
	const [manageUsersWarningDialogOpen, setManageUsersWarningDialogOpen] = useState(false);
	const [loading, setLoading] = useState(true);

	const init = () => {
		if (!roleId) return;
		axios.get(apiRoutes.getRoleById(roleId), { headers: authHeader })
			.then((response) => {
				setRole(response.data as RoleDetails);
				axios.get(apiRoutes.getPermissions, { headers: authHeader })
					.then((res) => {
						const list = res.data as Permission[];
						const rolePerms = response.data.permissions ?? [];
						list.forEach(permission => {
							rolePerms.forEach(rolePermission => {
								if (rolePermission.name === permission.name) {
									permission.active = true;
								}
							});
						});
						setRolePermissions(list);
						setLoading(false);
					})
					.catch((err) => {
						console.log(err);
					});
			})
			.catch((error) => {
				console.log(error);
			});
	};

	const toggleDeleteRoleDialog = () => {
		setDeleteRoleDialogOpen(!deleteRoleDialogOpen);
	};

	const deleteRole = () => {
		if (!roleId) return;
		axios.delete(apiRoutes.deleteRole(roleId), {
			headers: authHeader
		}).then(() => {
			updateCoAppNavigation(true, navigationItemReferences.roleNavItemIndex);
			navigate(pages.roleManagement, { replace: true });
			toggleDeleteRoleDialog();
		}).catch((err) => {
			console.log(err);
			handleToastAlert(messageLevels.ERROR, messages.ROLE_DELETION_ERROR_MSG);
			toggleDeleteRoleDialog();
		});
	};

	const toggleEditRoleDialog = () => {
		setEditRoleDialogOpen(!editRoleDialogOpen);
		setRoleNameInput("");
		setRoleNameError("");
	};

	const handleRoleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setRoleNameInput(e.target.value);
		setRoleNameError("");
	};

	const handleRoleNameChangeSubmit = () => {
		if (!roleNameInput) return;

		if (roleNameInput === state.name) {
			toggleEditRoleDialog();
			return;
		}

		if (!roleId) return;
		axios.put(apiRoutes.updateRole(roleId), { name: roleNameInput }, { headers: authHeader })
			.then(() => {
				toggleEditRoleDialog();
				updateCoAppNavigation(false, navigationItemReferences.roleNavItemIndex, roleNameInput, pathname);
			})
			.catch((e: unknown) => {
				console.log(e);
				const msg = axios.isAxiosError(e) ? String(e.response?.data?.message ?? "") : "";
				if (msg.includes("already exists")) {
					setRoleNameError(messages.RESOURCE_NAME_ALREADY_EXISTS_ERROR_MSG("Role"));
					return;
				}
				toggleEditRoleDialog();
				handleToastAlert(messageLevels.ERROR, messages.ROLE_NAME_UPDATE_ERROR_MSG);
			});
	};

	const handleCurrentUserPermissionUpdate = () => {
		if (currentUserId == null) return;
		axios.get(apiRoutes.getUser(1, currentUserId, "", ""), { headers: authHeader })
			.then(res => {
				dispatch(setPermissions(res.data.permissions));
			})
			.catch(err => console.log(err));
	};

	const togglePermission = (permissionInput: Permission | Record<string, never>, bypassCheck = true) => {
		let permission = permissionInput as Permission;
		if (permission?.name?.includes("MANAGE_USERS") && permission?.active && !bypassCheck) {
			const countOfManageUsersPermissions = currentUserPermissions.filter(p => p.includes("MANAGE_USERS")).length;
			if (countOfManageUsersPermissions === 1) {
				setManageUsersWarningDialogOpen(true);
				return;
			}
		}
		/* Empty object when user confirms MANAGE_USERS removal in warning modal */
		if (Object.keys(permissionInput).length === 0) {
			const manageUsersPermission = rolePermissions.filter(p => p.name?.includes("MANAGE_USERS"));
			const found = manageUsersPermission[0];
			if (!found) return;
			permission = found;
		}
		if (!roleId) return;
		axios.put(apiRoutes.updateRolePermission(roleId, permission.id), { active: !permission.active }, { headers: authHeader })
			.then(() => {
				setPermissionSeed(n => n + 1);
				//TODO:: placeholder, update navigate to expected destination once determined
				if (permission.name?.includes("MANAGE_USERS") && manageUsersWarningDialogOpen) {
					navigate(pages.home);
				}

				const users = role.users ?? [];
				const currentUserIsInCurrentRolesUsers = users.filter(user => user.id === currentUserId).length > 0;
				if (currentUserIsInCurrentRolesUsers) {
					handleCurrentUserPermissionUpdate();
				}
			})
			.catch((error) => {
				console.log(error);
				handleToastAlert(messageLevels.ERROR, permission.active ? messages.PERMISSION_REMOVED_ERROR_MSG : messages.PERMISSION_ADDED_ERROR_MSG);
			});
	};

	useEffect(() => {
		init();
	}, [permissionSeed, state.name]);

	const groupedPermissions = useMemo(() => {
		const out: Record<string, Permission[]> = {};
		rolePermissions.forEach(permission => {
			const section = sectionForCategory(permission.category);
			if (!out[section]) {
				out[section] = [];
			}
			out[section].push(permission);
		});
		return out;
	}, [rolePermissions]);

	// Sections in our preferred order, with any unmapped ones appended.
	const orderedSections = useMemo(() => {
		const present = Object.keys(groupedPermissions);
		const ordered = SECTION_ORDER.filter(s => present.includes(s));
		const leftovers = present.filter(s => !SECTION_ORDER.includes(s));
		return [...ordered, ...leftovers];
	}, [groupedPermissions]);
	return (
		<>
			<CoAppDatagridHeader
				title={`${state.name} Permissions Management`}
				resourceType="Role"
				subResourceType="Permissions"
				addIsPresent={false}
				editIsPresent={state.isSuperAdmin ? false : true}
				editOnClickHandler={toggleEditRoleDialog}
				deleteIsPresent={state.isSuperAdmin ? false : true}
				deleteOnClickHandler={toggleDeleteRoleDialog}
				hasDescription={true}
				description={`Manage permissions for ${state.name}.`}
			/>
			{
				loading ?
					<PermissionsContainer ref={containerRef} height={containerHeight}>
						{
							fakePermissions.map((fakePermission, index) => (
								<div key={fakePermission.id}>
									<Skeleton variant="text" width={200} height={50} sx={{ marginLeft: "25px", marginTop: "20px", marginBottom: "20px" }} />
									{
										Array.from({ length: index % 2 === 0 ? 1 : 2 }, (_, i) => (
											<>
												<Stack direction="row" spacing={2} sx={{ marginLeft: "25px", marginBottom: "2px" }}>
													<Skeleton key={i} variant="rectangular" width={100} height={25} sx={{ marginLeft: "25px", borderRadius: "10px" }} />
													<Skeleton key={i} variant="rectangular" width={400} height={25} sx={{ marginLeft: "25px", borderRadius: "10px" }} />
												</Stack>
												<Skeleton variant="text" width={400} height={20} sx={{ marginLeft: "25px", marginBottom: "10px" }} />
											</>
										))
									}
								</div>
							))
						}
					</PermissionsContainer>
					:
					<PermissionsContainer ref={containerRef} height={containerHeight}>
						{/* QA fix: page content is centered in a constrained column. */}
						<div style={{ maxWidth: 760, width: "100%", margin: "0 auto", padding: "8px 24px 32px" }}>
							{orderedSections.map((section) => (
								<div key={section} style={{ padding: "16px 20px", marginTop: 16, background: "#FFFFFF", border: "1px solid rgba(13, 31, 48, 0.08)", borderRadius: 12 }}>
									{/* Clean, descriptive section header (no faint grey uppercase
									    label). Permissions are consolidated into logical sections
									    via SECTION_FOR_CATEGORY. */}
									<Typography
										sx={{
											fontSize: 15,
											fontWeight: 700,
											color: "text.primary",
											mb: 1
										}}
									>
										{section}
									</Typography>
									{/* QA fix: one permission per line — title (+ info tooltip) on
									    the LEFT, toggle on the RIGHT (space-between). */}
									<div style={{ display: "flex", flexDirection: "column" }}>
										{groupedPermissions[section].map((permission, index) => (
											<div
												key={index}
												style={{
													display: "flex",
													alignItems: "center",
													justifyContent: "space-between",
													gap: 16,
													padding: "10px 0"
												}}
											>
												<div style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
													<Typography variant="body2" sx={{ fontWeight: 400, color: "text.primary" }}>
														{permission.friendlyName || permission.name}
													</Typography>
													{permission.description ? (
														<Tooltip title={permission.description} arrow placement="top">
															<InfoOutlined
																sx={{ fontSize: 16, color: "text.disabled", cursor: "help" }}
															/>
														</Tooltip>
													) : null}
												</div>
												<Switch
													size="small"
													checked={permission.active}
													onChange={() => togglePermission(permission, false)}
												/>
											</div>
										))}
									</div>
								</div>
							))}
						</div>
					</PermissionsContainer>
			}
			<CoAppDestructiveConfirmationModal
				dialogOpen={deleteRoleDialogOpen}
				dialogTitle={`Delete ${state.name} role?`}
				dialogMessage={messages.DELETION_CONFIRMATION_MSG(state.name, "role")}
				confirmClickHandler={deleteRole}
				cancelClickHandler={toggleDeleteRoleDialog}
				actionText="Delete"
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={manageUsersWarningDialogOpen}
				dialogTitle="Warning: You will no longer be able to manage permissions"
				dialogMessage={messages.REMOVE_MANAGE_USERS_ROLE_MSG(state.name)}
				confirmClickHandler={() => togglePermission({})}
				cancelClickHandler={() => setManageUsersWarningDialogOpen(false)}
				actionText="Proceed"
			/>
			<CoAppEditNameModal
				dialogOpen={editRoleDialogOpen}
				dialogTitle="Edit role name"
				changeHandler={handleRoleNameChange}
				placeholderText={state.name}
				confirmClickHandler={handleRoleNameChangeSubmit}
				cancelClickHandler={toggleEditRoleDialog}
				actionText="Save"
				editNameError={roleNameError}
				setEditNameError={setRoleNameError}
			/>
		</>
	);
}